import * as THREE from "three";
import { computeKinematics } from "../forwardKinematics";
import { getSceneState } from "../../stores/sceneStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import type { BodyUUID } from "../../types/scene";
import { getPhysicsWorker } from "../../physics/workerHandle";
import { logger } from "../../utils/logger";
import { quatAngleError } from "../orientationTargets";
import { dispatchIkSolveStatus, dispatchPickPlaceStatus } from "./events";
import { getScalarJointRef } from "./jointUtils";
import type { RankedGraspWorldZ } from "./graspRanking";
import { computePlacedInTrayNow } from "./spatialUtils";
import { setActiveServoSession, stopActiveServoSession } from "./servoSession";
import { createGripperCommandTracker } from "./gripperCommandTracker";
import { createPickPlaceServoConfig } from "./pickPlaceServoConfig";
import type { PickPlaceStage } from "./pickPlaceServoConfig";
import { createRuntimeBodyResolver } from "./runtimeBodyResolver";
import type { BodyRecord } from "./types";
import { isPickplaceDebugEnabled, logSelectedGrasp } from "./pickPlaceServoDebug";
import { createPickPlaceServoSetup } from "./pickPlaceServoSetup";
import {
  resolveGraspCandidatesWorldZ,
  selectReachableGraspWorldZ,
} from "./pickPlaceGraspSelection";
import {
  createIkTargets,
  isDropoffStage,
  resolveBaseTargetPosZ,
  resolveDesiredRotationZ,
  resolveDesiredZOffset,
  resolveTargetStageZ,
} from "./pickPlaceStageTargets";
import { solvePickPlaceIkAttempt } from "./pickPlaceIkSolver";
import { applyPickPlaceJointCommand } from "./pickPlaceJointCommand";

export function startServoToTargetObject(args: {
  targetBodyUuid: BodyUUID;
  endEffectorUuid: BodyUUID;
  useGripperTargetSite: boolean;
  dropoffBodyUuid?: BodyUUID | null;
}) {
  const setup = createPickPlaceServoSetup(args);
  if (!setup) return;
  const {
    targetBodyUuid,
    endEffectorUuid,
    useGripperTargetSite,
    dropoffBodyUuid,
    physicsWorker,
    yToZ,
    zToY,
    chain,
    dofLinks,
    dofKindByIndex,
    dofLimitByIndex,
    gripperForwardLocal,
    gripperUpLocal,
    elbowLinkIdx,
    elbowDofIndex,
    shoulderLiftDofIndex,
    shoulderPanLinkIdx,
    shoulderPanDofIndex,
  } = setup;

  const {
    hoverHeight,
    approachHeight,
    finalHeight,
    liftDelta,
    carryHoverHeight,
    dropApproachHeight,
    carryLiftHeight,
    xyTolHover,
    zTolHover,
    rotTolHover,
    xyTolApproach,
    zTolApproach,
    rotTolApproach,
    approachSettleSeconds,
    finalSettleSeconds,
    finalRelSpeedTol,
    closeHoldSeconds,
    liftSettleSeconds,
    graspRetryLiftMinY,
    graspRetryMaxEeCubeDist,
    graspRetryMinLiftDelta,
    graspRetryDecisionDelaySeconds,
    graspRetryFailureWindowSeconds,
    dropApproachSettleSeconds,
    openHoldSeconds,
    approachAnchorReacquireDistance,
    approachStageTimeoutSeconds,
    graspFinalDownBias,
    maxMissingTargetPoseTicks,
    maxIkSolveFailuresBeforeRecovery,
    intervalMs,
  } = createPickPlaceServoConfig(useGripperTargetSite);
  let stage: PickPlaceStage = "hover";
  let lastTick = performance.now();
  let lastSolution: number[] | null = null;
  let closeStart: number | null = null;
  let approachInPosStart: number | null = null;
  let finalInPosStart: number | null = null;
  let liftInPosStart: number | null = null;
  let dropInPosStart: number | null = null;
  let openStart: number | null = null;
  let approachStageStart: number | null = null;
  let lastRelZ: THREE.Vector3 | null = null;
  let graspAnchorZ: THREE.Vector3 | null = null;
  let approachAnchorZ: THREE.Vector3 | null = null;
  let lockedPickGrasp: RankedGraspWorldZ | null = null;
  let graspCandidateOffset = 0;
  // Persist the last successfully-computed FK so that a single IK solve failure doesn't
  // wipe out the in-position settle timer (the arm hasn't moved in 33ms so the pose is still valid).
  let lastValidFkAfter: ReturnType<typeof computeKinematics> | null = null;
  // Lock the roll orientation choice once the arm enters approach so the IK target
  // doesn't flip between roll-equivalent orientations each tick (which causes wrist
  // oscillation that prevents the approach/final settle timers from completing).
  let lockedDesiredRotZ: THREE.Quaternion | null = null;
  let liftStageStart: number | null = null;
  let liftFailStart: number | null = null;
  let cubeYAtCloseStart: number | null = null;
  let maxCubeYSinceClose: number | null = null;

  // Debug: stage transition logging (one line per transition, no per-tick spam).
  let stageLogged: PickPlaceStage | null = null;
  let stageLoggedAt: number | null = null;
  let selectedGraspLoggedKey: string | null = null;
  let approachDiagLoggedAt: number | null = null;
  let finalDiagLoggedAt: number | null = null;
  let finalArmLoggedAt: number | null = null;
  let warnedMissingDropoffPose = false;
  let warnedMissingDropoffRuntime = false;
  let missingTargetPoseTicks = 0;
  let consecutiveIkSolveFailures = 0;

  const trayBox = new THREE.Box3();
  const {
    resolveRuntimeObject,
    resolveBodyWorldPositionScene,
    resolveBodyWorldPositionSceneWithFallback,
  } = createRuntimeBodyResolver();
  const gripperCommands = createGripperCommandTracker({
    physicsWorker,
    isDebugEnabled: isPickplaceDebugEnabled,
    getStage: () => stage,
  });

  const tick = () => {
    const physicsNow = usePhysicsStore.getState();
    const running = physicsNow.isRunning === true;
    const workerNow: Worker | null = getPhysicsWorker();
    if (!running || !workerNow || workerNow !== physicsWorker) {
      stopActiveServoSession();
      return;
    }

    const now = performance.now();
    const dt = Math.max(1e-3, (now - lastTick) / 1000);
    lastTick = now;

    const targetPosScene = resolveBodyWorldPositionSceneWithFallback(targetBodyUuid);
    if (!targetPosScene) {
      missingTargetPoseTicks += 1;
      if (missingTargetPoseTicks >= maxMissingTargetPoseTicks) {
        const s = getSceneState();
        const targetBodyName = ((s?.bodies || {}) as BodyRecord)[targetBodyUuid]?.name || null;
        logger.warn("[ik-pickplace] TARGET_POSE_MISSING: stopping servo session", {
          targetBodyUuid,
          targetBodyName,
          stage,
          missingTicks: missingTargetPoseTicks,
        });
        dispatchPickPlaceStatus("TARGET_POSE_MISSING");
        stopActiveServoSession();
      }
      return;
    }
    missingTargetPoseTicks = 0;
    const dropoffPosScene = dropoffBodyUuid
      ? resolveBodyWorldPositionSceneWithFallback(dropoffBodyUuid)
      : null;
    const usingDropoffTarget = isDropoffStage(stage);
    const activeTargetPosScene = usingDropoffTarget ? dropoffPosScene : targetPosScene;
    if (!activeTargetPosScene) {
      if (usingDropoffTarget && dropoffBodyUuid && !warnedMissingDropoffPose) {
        const s = getSceneState();
        const dropoffBodyName = ((s?.bodies || {}) as BodyRecord)[dropoffBodyUuid]?.name || null;
        logger.warn("[ik-pickplace] DROPOFF_POSE_MISSING: cannot resolve dropoff target pose", {
          dropoffBodyUuid,
          dropoffBodyName,
          stage,
          targetBodyUuid,
        });
        warnedMissingDropoffPose = true;
      }
      return;
    }
    warnedMissingDropoffPose = false;
    gripperCommands.maybeOpenOnce();

    const sceneState = getSceneState();
    const currentRefs = dofLinks.map((link) => {
      const j = link.jointUuid ? sceneState.joints?.[link.jointUuid] : null;
      return getScalarJointRef(j);
    });
    const seed =
      lastSolution && lastSolution.length === chain.dofCount ? lastSolution : currentRefs;
    const fkSeed = computeKinematics(chain, seed);

    // Compute stage target in Z-up (MuJoCo) space to avoid axis confusion.
    const targetPosZ = targetPosScene.clone().applyQuaternion(yToZ);
    const activeTargetPosZ = activeTargetPosScene.clone().applyQuaternion(yToZ);

    // Resolve ranked grasp poses for the pick target (if grasp data is available).
    // Only compute while targeting the pick object — not during drop/carry stages.
    const isPickStage = !usingDropoffTarget;
    const graspCandidatesWorldZ = resolveGraspCandidatesWorldZ({
      isPickStage,
      targetBodyUuid,
      resolveRuntimeObject,
      fkSeed,
      yToZ,
      zToY,
    });
    const graspWorldZ =
      Array.isArray(graspCandidatesWorldZ) && graspCandidatesWorldZ.length > 0
        ? graspCandidatesWorldZ[0]
        : null;
    const isGraspDrivenPick = isPickStage && !!graspWorldZ;
    const isNearGroundGraspPick = isGraspDrivenPick && targetPosScene.y <= 0.05;
    const hoverXyTolNow = isGraspDrivenPick ? Math.max(xyTolHover, 0.14) : xyTolHover;
    const hoverZTolNow = isGraspDrivenPick ? Math.max(zTolHover, 0.1) : zTolHover;
    const approachXyTolNow = isGraspDrivenPick ? Math.max(xyTolApproach, 0.02) : xyTolApproach;
    const approachZTolNow = isGraspDrivenPick ? Math.max(zTolApproach, 0.02) : zTolApproach;

    // During approach/final, latch the pick target so tiny object drift does not
    // keep resetting settle timers ("chasing"). Reacquire only on larger movement.
    if (stage === "approach" || stage === "final") {
      if (!approachAnchorZ) {
        approachAnchorZ = targetPosZ.clone();
      } else if (approachAnchorZ.distanceTo(targetPosZ) > approachAnchorReacquireDistance) {
        approachAnchorZ = targetPosZ.clone();
      }
    }
    // IMPORTANT: once we close on the object, the object tends to move with the gripper.
    // If we keep targeting the *live* object pose during lift, we can create a feedback loop
    // where the target "moves up" as we lift, causing an endless climb.
    const baseTargetPosZ = resolveBaseTargetPosZ({
      stage,
      graspAnchorZ,
      approachAnchorZ,
      targetPosZ,
      activeTargetPosZ,
    });
    const desiredZOffset = resolveDesiredZOffset({
      stage,
      dropoffBodyUuid,
      hoverHeight,
      approachHeight,
      finalHeight,
      liftDelta,
      carryHoverHeight,
      dropApproachHeight,
      carryLiftHeight,
    });

    // If grasp candidates are available, choose one that is IK-reachable for the current stage
    // instead of always forcing the top-ranked candidate. This prevents deadlocks when the
    // preferred grasp is kinematically awkward from the current arm state.
    const selectedGraspWorldZ = selectReachableGraspWorldZ({
      isPickStage,
      graspCandidatesWorldZ,
      lockedPickGrasp,
      stage,
      graspCandidateOffset,
      approachHeight,
      chain,
      seed,
    });
    if (isPickStage && selectedGraspWorldZ) {
      selectedGraspLoggedKey = logSelectedGrasp({
        targetBodyUuid,
        stage,
        selectedGraspWorldZ,
        graspCandidateOffset,
        candidateCount: Array.isArray(graspCandidatesWorldZ) ? graspCandidatesWorldZ.length : 0,
        locked: !!lockedPickGrasp,
        selectedGraspLoggedKey,
      });
    }

    const targetStageZ = resolveTargetStageZ({
      selectedGraspWorldZ,
      stage,
      hoverHeight,
      approachHeight,
      graspFinalDownBias,
      baseTargetPosZ,
      desiredZOffset,
    });
    const { candidateDesiredRotZ, enforceGraspOrientation } = resolveDesiredRotationZ({
      selectedGraspWorldZ,
      isPickStage,
      stage,
      fkSeed,
      gripperForwardLocal,
      gripperUpLocal,
    });
    // Lock the roll choice when entering approach/final so it doesn't flip between
    // roll-equivalent orientations as the wrist moves, which resets the settle timers.
    if (stage === "approach" || stage === "final") {
      if (!lockedDesiredRotZ) lockedDesiredRotZ = candidateDesiredRotZ;
    } else {
      lockedDesiredRotZ = null;
    }
    const desiredRotZ = lockedDesiredRotZ ?? candidateDesiredRotZ;

    const { poseTarget, posTarget } = createIkTargets({ targetStageZ, desiredRotZ });
    const attempt = solvePickPlaceIkAttempt({
      chain,
      poseTarget,
      posTarget,
      seed,
      fkSeed,
      desiredRotZ,
      enforceGraspOrientation,
      isNearGroundGraspPick,
      stage,
      activeTargetPosScene,
      zToY,
      dofKindByIndex,
      dofLimitByIndex,
      elbowLinkIdx,
      elbowDofIndex,
      shoulderLiftDofIndex,
      shoulderPanLinkIdx,
      shoulderPanDofIndex,
    });

    // Accept max_iterations results that are close enough — the solver made progress but hit the
    // iteration cap. Velocity limiting means the arm only steps a small fraction toward the
    // solution each tick anyway, so even a partially-converged solution is a valid step.
    // We still reject "failed" results (null delta / ill-conditioned Jacobian) that have large error.
    const attemptIsValid = !!attempt && attempt.error <= 0.08;
    if (attemptIsValid) {
      consecutiveIkSolveFailures = 0;
      dispatchIkSolveStatus("success", attempt?.error);
    } else {
      consecutiveIkSolveFailures += 1;
      if (consecutiveIkSolveFailures >= 10) {
        dispatchIkSolveStatus("failed", attempt?.error);
      }
      // Disturbance recovery: if we can't solve while descending for a while, back out and re-approach.
      if (
        consecutiveIkSolveFailures >= maxIkSolveFailuresBeforeRecovery &&
        (stage === "approach" || stage === "final")
      ) {
        if (
          isPickStage &&
          Array.isArray(graspCandidatesWorldZ) &&
          graspCandidatesWorldZ.length > 1
        ) {
          graspCandidateOffset = (graspCandidateOffset + 1) % graspCandidatesWorldZ.length;
        }
        stage = "hover";
        closeStart = null;
        approachAnchorZ = null;
        approachStageStart = null;
        lockedPickGrasp = null;
        lockedDesiredRotZ = null;
        lastValidFkAfter = null;
        approachInPosStart = null;
        finalInPosStart = null;
        liftInPosStart = null;
        dropInPosStart = null;
        openStart = null;
        lastRelZ = null;
        graspAnchorZ = null;
      }
    }

    let fkAfter: ReturnType<typeof computeKinematics> | null = null;
    if (attemptIsValid) {
      const commandResult = applyPickPlaceJointCommand({
        attempt,
        chain,
        dofLinks,
        currentRefs,
        lastSolution,
        dt,
      });
      lastSolution = commandResult.lastSolution;
      fkAfter = commandResult.fkAfter;
      lastValidFkAfter = fkAfter;
    }
    // Use the last successful FK when the current tick failed — the arm hasn't moved in 33ms
    // so using the previous pose for stage checks is valid and prevents settle timers from stalling.
    const fkForStage = fkAfter ?? lastValidFkAfter;
    // Stage transition: once we're stably above the target, descend.
    if (stage === "hover" || stage === "approach" || stage === "final") {
      try {
        gripperCommands.open();
      } catch {
        // ignore
      }
    }

    // Keep grip force asserted while translating to the dropoff target.
    if (stage === "carry_hover" || stage === "drop_approach") {
      try {
        gripperCommands.close();
      } catch {
        // ignore
      }
    }
    if (stage === "hover" || stage === "carry_hover") {
      try {
        if (!fkForStage) return;
        const eeZ = fkForStage.endEffector.position;
        const xyErr = new THREE.Vector2(eeZ.x - targetStageZ.x, eeZ.y - targetStageZ.y).length();
        const zErr = Math.abs(eeZ.z - targetStageZ.z);
        const rotErr =
          fkForStage.endEffector.rotation instanceof THREE.Quaternion
            ? quatAngleError(fkForStage.endEffector.rotation, desiredRotZ)
            : Infinity;
        // For grasp-driven pick stages, don't block descent on orientation at hover.
        // Some objects (e.g. thin plates) can select a grasp orientation that is hard to
        // satisfy at hover, which can deadlock the stage machine before approach/final.
        // We still enforce orientation in final stage before close.
        const allowHoverDescentWithoutRot = stage === "hover" && isGraspDrivenPick;
        const hoverRotOk = allowHoverDescentWithoutRot || rotErr < rotTolHover;
        if (xyErr < hoverXyTolNow && zErr < hoverZTolNow && hoverRotOk) {
          const nextStage = stage === "hover" ? "approach" : "drop_approach";
          stage = nextStage;
          approachAnchorZ = nextStage === "approach" ? targetPosZ.clone() : null;
          approachInPosStart = null;
          dropInPosStart = null;
          if (nextStage === "approach") {
            approachStageStart = now;
            if (!lockedPickGrasp && selectedGraspWorldZ) {
              lockedPickGrasp = selectedGraspWorldZ;
            }
          } else {
            approachStageStart = null;
            lockedPickGrasp = null;
          }
        }
      } catch {
        // ignore; keep hovering
      }
    }

    // When we reach approach height, close the gripper briefly before lifting.
    if (stage === "approach") {
      try {
        if (!fkForStage) return;
        if (approachStageStart == null) approachStageStart = now;
        if ((now - approachStageStart) / 1000 >= approachStageTimeoutSeconds) {
          if (
            isPickStage &&
            Array.isArray(graspCandidatesWorldZ) &&
            graspCandidatesWorldZ.length > 1
          ) {
            graspCandidateOffset = (graspCandidateOffset + 1) % graspCandidatesWorldZ.length;
          }
          stage = "hover";
          closeStart = null;
          approachAnchorZ = null;
          approachStageStart = null;
          lockedPickGrasp = null;
          lockedDesiredRotZ = null;
          lastValidFkAfter = null;
          approachInPosStart = null;
          finalInPosStart = null;
          liftInPosStart = null;
          dropInPosStart = null;
          openStart = null;
          lastRelZ = null;
          graspAnchorZ = null;
          logger.warn("[ik-pickplace] APPROACH_TIMEOUT: retrying with next grasp candidate", {
            targetBodyUuid,
            graspCandidateOffset,
            candidateCount: Array.isArray(graspCandidatesWorldZ) ? graspCandidatesWorldZ.length : 0,
            timeoutSeconds: approachStageTimeoutSeconds,
          });
          dispatchPickPlaceStatus("APPROACH_TIMEOUT");
          return;
        }
        const eeZ = fkForStage.endEffector.position;
        const xyErr = new THREE.Vector2(eeZ.x - targetStageZ.x, eeZ.y - targetStageZ.y).length();
        const zErr = Math.abs(eeZ.z - targetStageZ.z);
        const inPos = xyErr < approachXyTolNow && zErr < approachZTolNow;

        // Throttled diagnostic: log once per second so we can see whether position is
        // converging or whether the IK attempt is failing before reaching the target.
        if (
          !approachInPosStart &&
          (approachDiagLoggedAt == null || now - approachDiagLoggedAt > 1000)
        ) {
          approachDiagLoggedAt = now;
          logger.debug("[ik-approach] progress", {
            xyErrMm: Math.round(xyErr * 1000),
            zErrMm: Math.round(zErr * 1000),
            ikOk: attemptIsValid,
            ikErr: attempt ? Math.round((attempt.error ?? 0) * 10000) / 10000 : null,
            ikStatus: attempt?.status ?? null,
            consecutiveFail: consecutiveIkSolveFailures,
          });
        }

        // We only use hover/approach as a staging move; don't require perfect orientation here.
        if (!inPos) {
          approachInPosStart = null;
        } else {
          if (approachInPosStart == null) approachInPosStart = now;
          const held = (now - approachInPosStart) / 1000;
          if (held >= approachSettleSeconds) {
            stage = "final";
            approachStageStart = null;
            approachInPosStart = null;
            finalInPosStart = null;
            lastRelZ = null;
          }
        }
      } catch {
        // ignore; keep approaching
      }
    }

    if (stage === "drop_approach") {
      try {
        if (!fkForStage) return;
        const eeZ = fkForStage.endEffector.position;
        const xyErr = new THREE.Vector2(eeZ.x - targetStageZ.x, eeZ.y - targetStageZ.y).length();
        const zErr = Math.abs(eeZ.z - targetStageZ.z);
        const inPos = xyErr < approachXyTolNow && zErr < approachZTolNow;
        if (!inPos) {
          dropInPosStart = null;
        } else {
          if (dropInPosStart == null) dropInPosStart = now;
          const held = (now - dropInPosStart) / 1000;
          if (held >= dropApproachSettleSeconds) {
            stage = "drop_open";
            openStart = now;
            dropInPosStart = null;
            logger.info("[ik-pickplace] DROP_OPEN: intentional release", {
              targetBodyUuid,
              dropoffBodyUuid,
              openHoldSeconds,
            });
          }
        }
      } catch {
        // ignore; keep approaching
      }
    }

    // Final descend: align spheres (target height 0) before closing.
    if (stage === "final") {
      try {
        if (!fkForStage) return;
        const eeZ = fkForStage.endEffector.position;
        const xyErr = new THREE.Vector2(eeZ.x - targetStageZ.x, eeZ.y - targetStageZ.y).length();
        const zErr = Math.abs(eeZ.z - targetStageZ.z);
        const rotErr =
          fkForStage.endEffector.rotation instanceof THREE.Quaternion
            ? quatAngleError(fkForStage.endEffector.rotation, desiredRotZ)
            : Infinity;
        const finalRotTolNow = isGraspDrivenPick ? Math.max(rotTolApproach, 0.11) : rotTolApproach;
        const inPos = xyErr < xyTolApproach && zErr < zTolApproach && rotErr < finalRotTolNow;

        // Close when: IK has converged (inPos), arm is settled (relSpeedOk), and the
        // ACTUAL PHYSICS position of the end-effector body is close to cube level.
        //
        // inPos uses commanded FK which leads the physical arm (physics has PD lag).
        // So we add a runtime body position check: get the real world Y of the EE body
        // from the scene graph (which reflects actual physics state), then account for
        // the site offset to estimate where the gripper_target physically is.
        let relSpeed = 0;
        try {
          const relZ = targetPosZ.clone().sub(eeZ);
          if (lastRelZ) {
            relSpeed = relZ.clone().sub(lastRelZ).length() / Math.max(1e-3, dt);
          }
          lastRelZ = relZ;
        } catch {
          // ignore
        }
        const relSpeedOk = relSpeed < finalRelSpeedTol;
        // Site offset magnitude: how far the gripper_target is from the EE body origin.
        // When the gripper points downward, this is approximately how much lower the
        // site is vs the EE body in world Y.
        const siteOffsetMag = Array.isArray(chain.endEffectorOffset)
          ? new THREE.Vector3(
              chain.endEffectorOffset[0] as number,
              chain.endEffectorOffset[1] as number,
              chain.endEffectorOffset[2] as number
            ).length()
          : 0;
        const eeBodyRuntimeScene = resolveBodyWorldPositionScene(endEffectorUuid);
        // EE body must be at most (site offset + 20mm margin) above cube center — i.e.,
        // the physical gripper body is actually close to the cube, not still descending.
        const runtimeAtCubeHeight = isGraspDrivenPick
          ? true
          : eeBodyRuntimeScene != null
            ? eeBodyRuntimeScene.y <= targetPosScene.y + siteOffsetMag + 0.02
            : true;
        const readyToClose = inPos && relSpeedOk && runtimeAtCubeHeight;
        if (
          isPickplaceDebugEnabled() &&
          (finalDiagLoggedAt == null || now - finalDiagLoggedAt > 200)
        ) {
          finalDiagLoggedAt = now;
          logger.info("[ik-pickplace] FINAL_GATES", {
            readyToClose,
            inPos,
            relSpeedOk,
            runtimeAtCubeHeight,
            xyErrMm: Math.round(xyErr * 1000),
            zErrMm: Math.round(zErr * 1000),
            rotErrDeg: +((rotErr * 180) / Math.PI).toFixed(2),
            relSpeed: Number(relSpeed.toFixed(4)),
            eeBodyRuntimeYMm:
              eeBodyRuntimeScene != null ? Math.round(eeBodyRuntimeScene.y * 1000) : null,
            cubeYMm: Math.round(targetPosScene.y * 1000),
            thresholdMm: Math.round((targetPosScene.y + siteOffsetMag + 0.02) * 1000),
          });
        }
        if (!readyToClose) {
          finalInPosStart = null;
          finalArmLoggedAt = null;
        } else {
          if (isPickplaceDebugEnabled() && finalArmLoggedAt == null) {
            finalArmLoggedAt = now;
            logger.info("[ik-pickplace] CLOSE_ARMED", {
              inPos,
              relSpeedOk,
              runtimeAtCubeHeight,
              xyErrMm: Math.round(xyErr * 1000),
              zErrMm: Math.round(zErr * 1000),
              relSpeed: Number(relSpeed.toFixed(4)),
              eeBodyRuntimeYMm:
                eeBodyRuntimeScene != null ? Math.round(eeBodyRuntimeScene.y * 1000) : null,
              cubeYMm: Math.round(targetPosScene.y * 1000),
              thresholdMm: Math.round((targetPosScene.y + siteOffsetMag + 0.02) * 1000),
            });
          }
          if (finalInPosStart == null) finalInPosStart = now;
          const held = (now - finalInPosStart) / 1000;
          if (held >= finalSettleSeconds) {
            stage = "close";
            closeStart = now;
            // Freeze the grasp target pose at close time (not object center).
            // This keeps lift anchored to the selected grasp instead of drifting
            // back toward the object's centroid after close.
            graspAnchorZ = targetStageZ.clone();
            finalInPosStart = null;
            const eeSceneClose = eeZ.clone().applyQuaternion(zToY);
            const closePayload = {
              eeScene: {
                x: +eeSceneClose.x.toFixed(4),
                y: +eeSceneClose.y.toFixed(4),
                z: +eeSceneClose.z.toFixed(4),
              },
              cubeScene: {
                x: +targetPosScene.x.toFixed(4),
                y: +targetPosScene.y.toFixed(4),
                z: +targetPosScene.z.toFixed(4),
              },
              xyErrMm: Math.round(xyErr * 1000),
              zErrMm: Math.round(zErr * 1000),
              rotErrDeg: +((rotErr * 180) / Math.PI).toFixed(1),
            };
            logger.debug("[ik-pickplace] CLOSE_TRIGGER", closePayload);
          }
        }
      } catch {
        // ignore; keep descending
      }
    }

    if (stage === "close") {
      // Re-send close for a short window to overcome missed control frames.
      // (Some runs can drop a single control post; repeatedly commanding close is cheap and robust.)
      try {
        gripperCommands.close();
      } catch {
        // ignore
      }
      if (closeStart != null && (now - closeStart) / 1000 >= closeHoldSeconds) {
        stage = "lift";
        liftStageStart = now;
        liftFailStart = null;
        cubeYAtCloseStart = Number.isFinite(Number(targetPosScene.y))
          ? Number(targetPosScene.y)
          : null;
        maxCubeYSinceClose = cubeYAtCloseStart;
        liftInPosStart = null;
      }
    }

    if (stage === "lift") {
      // Keep reasserting close while lifting to maintain grip force under load.
      try {
        gripperCommands.close();
      } catch {
        // ignore
      }
      if (dropoffBodyUuid && dropoffPosScene) {
        try {
          if (!fkForStage) return;
          const eeZ = fkForStage.endEffector.position;
          const xyErr = new THREE.Vector2(eeZ.x - targetStageZ.x, eeZ.y - targetStageZ.y).length();
          const zErr = Math.abs(eeZ.z - targetStageZ.z);
          const inPos = xyErr < xyTolHover && zErr < zTolHover;
          if (!inPos) {
            liftInPosStart = null;
          } else {
            if (liftInPosStart == null) liftInPosStart = now;
            const held = (now - liftInPosStart) / 1000;
            if (held >= liftSettleSeconds) {
              // Only proceed to tray if we actually lifted the cube.
              const eeScene = eeZ.clone().applyQuaternion(zToY);
              const eeCubeDist = eeScene.distanceTo(targetPosScene);
              const cubeLifted = targetPosScene.y >= graspRetryLiftMinY;
              const closeBaselineY = Number.isFinite(Number(cubeYAtCloseStart))
                ? Number(cubeYAtCloseStart)
                : null;
              const maxSinceCloseY = Number.isFinite(Number(maxCubeYSinceClose))
                ? Number(maxCubeYSinceClose)
                : null;
              const liftDeltaFromClose =
                closeBaselineY != null && maxSinceCloseY != null
                  ? maxSinceCloseY - closeBaselineY
                  : 0;
              const liftedFromClose = liftDeltaFromClose >= graspRetryMinLiftDelta;
              const graspLikely =
                cubeLifted && liftedFromClose && eeCubeDist <= graspRetryMaxEeCubeDist;
              if (graspLikely) {
                stage = "carry_hover";
                approachAnchorZ = null;
                liftStageStart = null;
                liftFailStart = null;
                liftInPosStart = null;
                approachInPosStart = null;
                finalInPosStart = null;
                dropInPosStart = null;
                logger.info("[ik-pickplace] GRASP_CONFIRMED", {
                  targetBodyUuid,
                  cubeY: targetPosScene.y,
                  closeBaselineY,
                  maxSinceCloseY,
                  liftDeltaFromClose,
                  eeCubeDist,
                  gripperCmd: gripperCommands.getLastCommand(),
                });
              } else {
                // Guard against premature retries: some grippers (e.g. Panda tendon drive)
                // need a bit longer to build holding force before object lift is visible.
                const inLiftForSeconds =
                  liftStageStart != null ? (now - liftStageStart) / 1000 : Number.POSITIVE_INFINITY;
                if (inLiftForSeconds < graspRetryDecisionDelaySeconds) {
                  liftInPosStart = null;
                  return;
                }
                if (liftFailStart == null) liftFailStart = now;
                const failedForSeconds = (now - liftFailStart) / 1000;
                if (failedForSeconds < graspRetryFailureWindowSeconds) {
                  liftInPosStart = null;
                  return;
                }
                const preOpenCmd = { ...gripperCommands.getLastCommand() };
                // Missed grasp: restart pick routine instead of going to tray empty-handed.
                try {
                  gripperCommands.open();
                } catch {
                  // ignore
                }
                stage = "hover";
                closeStart = null;
                approachAnchorZ = null;
                approachStageStart = null;
                lockedPickGrasp = null;
                liftStageStart = null;
                liftFailStart = null;
                cubeYAtCloseStart = null;
                maxCubeYSinceClose = null;
                liftInPosStart = null;
                approachInPosStart = null;
                finalInPosStart = null;
                dropInPosStart = null;
                openStart = null;
                lastRelZ = null;
                graspAnchorZ = null;
                logger.warn("[ik-pickplace] PICK_MISSED: retrying pickup before tray transfer", {
                  targetBodyUuid,
                  dropoffBodyUuid,
                  cubeY: targetPosScene.y,
                  eeCubeDist,
                  closeBaselineY,
                  maxSinceCloseY,
                  liftDeltaFromClose,
                  cubeLifted,
                  liftedFromClose,
                  inLiftForSeconds:
                    liftStageStart != null
                      ? Number(((now - liftStageStart) / 1000).toFixed(3))
                      : null,
                  failedForSeconds:
                    liftFailStart != null
                      ? Number(((now - liftFailStart) / 1000).toFixed(3))
                      : null,
                  preOpenGripperCmd: preOpenCmd,
                  postOpenGripperCmd: gripperCommands.getLastCommand(),
                  gripperCmd: gripperCommands.getLastCommand(),
                });
                dispatchPickPlaceStatus("PICK_MISSED");
              }
            }
          }
        } catch {
          // ignore; keep lifting
        }
      }
    }

    if (stage === "drop_open") {
      try {
        gripperCommands.open();
      } catch {
        // ignore
      }
      // After releasing, keep running briefly to see if we actually placed in the tray.
      // If the cube touches the ground outside the tray, restart the routine to recover.
      if (openStart != null && (now - openStart) / 1000 >= openHoldSeconds) {
        // Compute tray bounds if runtime object exists.
        const trayObj = dropoffBodyUuid ? resolveRuntimeObject(dropoffBodyUuid) : null;
        let trayBoxNow: THREE.Box3 | null = null;
        if (trayObj) {
          try {
            trayBox.setFromObject(trayObj);
            trayBoxNow = trayBox;
          } catch {
            trayBoxNow = null;
          }
        } else if (dropoffBodyUuid && !warnedMissingDropoffRuntime) {
          warnedMissingDropoffRuntime = true;
          const s = getSceneState();
          const dropoffBodyName = ((s?.bodies || {}) as BodyRecord)[dropoffBodyUuid]?.name || null;
          logger.warn(
            "[ik-pickplace] DROPOFF_RUNTIME_MISSING: cannot evaluate tray bounds for restart logic",
            {
              dropoffBodyUuid,
              dropoffBodyName,
            }
          );
        }

        const placed = computePlacedInTrayNow({
          cubePosScene: targetPosScene,
          trayBox: trayBoxNow,
        });
        // Ground contact proxy: cube half-size is 0.0195m (see `public/objects/cube/cube.xml`).
        const cubeHalf = 0.0195;
        const groundTouch = targetPosScene.y <= cubeHalf + 0.004;
        const trayDiagnostics = (() => {
          if (!trayBoxNow) return null;
          const min = trayBoxNow.min;
          const max = trayBoxNow.max;
          const eps = 0.015;
          const inXZ =
            targetPosScene.x >= min.x - eps &&
            targetPosScene.x <= max.x + eps &&
            targetPosScene.z >= min.z - eps &&
            targetPosScene.z <= max.z + eps;
          const yTop = max.y + 0.008;
          const belowTop = targetPosScene.y <= yTop;
          return {
            cube: {
              x: Number(targetPosScene.x.toFixed(4)),
              y: Number(targetPosScene.y.toFixed(4)),
              z: Number(targetPosScene.z.toFixed(4)),
            },
            trayMin: {
              x: Number(min.x.toFixed(4)),
              y: Number(min.y.toFixed(4)),
              z: Number(min.z.toFixed(4)),
            },
            trayMax: {
              x: Number(max.x.toFixed(4)),
              y: Number(max.y.toFixed(4)),
              z: Number(max.z.toFixed(4)),
            },
            inXZ,
            belowTop,
            yTop: Number(yTop.toFixed(4)),
          };
        })();

        if (placed) {
          stopActiveServoSession();
        } else if (groundTouch) {
          stage = "hover";
          closeStart = null;
          approachAnchorZ = null;
          approachStageStart = null;
          lockedPickGrasp = null;
          approachInPosStart = null;
          finalInPosStart = null;
          liftInPosStart = null;
          dropInPosStart = null;
          openStart = null;
          lastRelZ = null;
          graspAnchorZ = null;
          cubeYAtCloseStart = null;
          maxCubeYSinceClose = null;
          logger.warn(
            "[ik-pickplace] DROP_MISSED: cube touched ground outside tray; restarting routine",
            {
              targetBodyUuid,
              dropoffBodyUuid,
              placed,
              groundTouch,
              trayDiagnostics,
            }
          );
        } else {
          // Still in flight/settling: keep the session alive a bit longer.
          const postDropMaxSeconds = 2.2;
          if ((now - openStart) / 1000 >= openHoldSeconds + postDropMaxSeconds) {
            stopActiveServoSession();
          }
        }
      }
    }

    if (
      (stage === "close" ||
        stage === "lift" ||
        stage === "carry_hover" ||
        stage === "drop_approach") &&
      Number.isFinite(Number(targetPosScene.y))
    ) {
      const y = Number(targetPosScene.y);
      if (!Number.isFinite(Number(maxCubeYSinceClose))) {
        maxCubeYSinceClose = y;
      } else if (y > Number(maxCubeYSinceClose)) {
        maxCubeYSinceClose = y;
      }
    }

    // Debug: stage entry logging.
    if (stageLogged !== stage) {
      if (stage === "hover" || stage === "drop_open") {
        cubeYAtCloseStart = null;
        maxCubeYSinceClose = null;
      }
      const s = getSceneState();
      const bodies = (s?.bodies || {}) as BodyRecord;
      const targetBodyName = bodies[targetBodyUuid]?.name || null;
      const dropoffBodyName = dropoffBodyUuid ? bodies[dropoffBodyUuid]?.name || null : null;
      const prevStageSeconds = stageLoggedAt != null ? (now - stageLoggedAt) / 1000 : null;
      logger.info("[ik-pickplace] STAGE_ENTER", {
        from: stageLogged,
        to: stage,
        prevStageSeconds,
        targetBodyUuid,
        targetBodyName,
        dropoffBodyUuid,
        dropoffBodyName,
      });
      stageLogged = stage;
      stageLoggedAt = now;
    }

    // Keep servo running so it tracks a moving object and maintains the current stage.
    return;
  };

  const id = setInterval(tick, intervalMs);
  setActiveServoSession({
    stop: () => {
      clearInterval(id);
    },
  });
}
